import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd"
import { Add, Delete, Edit } from "@mui/icons-material"
import MenuIcon from "@mui/icons-material/Menu"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { DeleteDialog } from "components/DeleteDialog/DeleteDialog"
import { ReportNodeImageEditDialog } from "components/ReportNodeImage/ReportNodeImageEditDialog"
import { ReportNodeTableEditDialog } from "components/ReportNodeTable/ReportNodeTableEditDialog"
import { ReportNodeTextEditDialog } from "components/ReportNodeText/ReportNodeTextEditDialog"
import { Chapter, ChapterType, DataNode, NodeDataType, Report, TextBlock } from "graphql/types"
import { useGetTextBlocksForReport } from "hooks/getTextBlocks"
import { useStoreActions } from "hooks/storeHooks"
import { useEffect, useState } from "react"
import { deletableChapterTypes, editableChapterTypes } from "store/reportChaptersTemplate"
import { isNodeImage, isNodeTable, isNodeText } from "store/typeGuards"
import { maxSubChapterLevel } from "utils/constants"
import { findChapterLevelById } from "utils/findChapterLevelById"
import { updateReportChapterNumbering } from "utils/updateReportChapterNumbering"
import { updateReportNumbering } from "utils/updateReportNumbering"
import { v4 } from "uuid"
import { AddBlockToChapter } from "./AddBlockToChapter"
import { ReportDataContent } from "./ReportDataContent"

/**
 * Generates the next chapter number for a subchapter
 */
const generateNextChapterNumber = (
  parentChapterNumber: string,
  existingSubChapters: Chapter[]
): string => {
  const parentParts = parentChapterNumber.split(".")
  const nextIndex = existingSubChapters.length + 1

  if (parentParts.length === 1) {
    // Main chapter -> first level subchapter (1.1, 1.2, etc.)
    return `${parentChapterNumber}.${nextIndex}`
  } else if (parentParts.length === 2) {
    // First level subchapter -> second level subchapter (1.1.1, 1.1.2, etc.)
    return `${parentChapterNumber}.${nextIndex}`
  }

  // Should not happen as we limit to 2 levels
  return `${parentChapterNumber}.${nextIndex}`
}

type ReportContentCardProps = {
  chapter: Chapter
  report: Report
  // eslint-disable-next-line no-unused-vars
  updateReport: (updatedReport: Report) => void
}

export const ReportContent = (props: ReportContentCardProps) => {
  const { chapter, report, updateReport } = props
  const [newNode, setNewNode] = useState<DataNode>()
  const [anchorElAddMenu, setAnchorElAddMenu] = useState<null | HTMLElement>(null)
  const openAddMenu = Boolean(anchorElAddMenu)
  const [openAddTextBlockDialog, setOpenAddTextBlockDialog] = useState<TextBlock[]>([])
  const [openEditChapterDialog, setOpenEditChapterDialog] = useState(false)
  const [chapterName, setChapterName] = useState("")
  const [isEditMode, setIsEditMode] = useState(false) // true = edit existing, false = add new
  const [openDeleteChapterDialog, setOpenDeleteChapterDialog] = useState(false)
  const { fetchTextBlocks } = useStoreActions((state) => state)

  useEffect(() => {
    fetchTextBlocks({ fresh: false })
  }, [fetchTextBlocks])

  const textBlocksForChapter = useGetTextBlocksForReport(
    chapter.type,
    report.examinations.map((examination) => examination.examinationConfig.id),
    report.examinations.map((examination) => examination.norm.id)
  ).filter(
    (textBlock) =>
      !textBlock.block.some((blockData) =>
        chapter.data.some((chapterData) => chapterData.id === blockData.id)
      )
  )

  const handleClickAddMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElAddMenu(event.currentTarget)
  }
  const handleCloseAddMenu = () => {
    setAnchorElAddMenu(null)
  }

  const getChapterLevel = (chapterId: string): "h3" | "h4" | "h5" | "h6" | "body1" => {
    const level = findChapterLevelById(report.chapter, chapterId)
    switch (level) {
      case 0:
        return "h4"
      case 1:
        return "h5"
      case 2:
        return "h6"
      default:
        return "body1"
    }
  }

  const updateChapter = (chapters: Chapter[], newChapter: Chapter): Chapter[] => {
    return chapters.map((chapter) => {
      if (chapter.id === newChapter.id) {
        return newChapter
      }

      if (chapter.subChapter && chapter.subChapter.length > 0) {
        return {
          ...chapter,
          subChapter: updateChapter(chapter.subChapter, newChapter),
        }
      }

      return chapter
    })
  }

  const handleOpenAddNode = (type: NodeDataType, data?: DataNode) => {
    handleCloseAddMenu()
    if (data) {
      setNewNode(data)
    } else {
      const newNode: DataNode = {
        id: v4(),
        dataType: type,
        editable: true,
        deletable: true,
        image: [],
        imageCaption: [],
        imageNumber: [],
        tableBody: [],
        tableCaption: "",
        tableHeader: [],
        tableNumber: 1,
        text: "",
        isAutoGenerated: false,
        sourceExaminationId: undefined,
      }
      setNewNode(newNode)
    }
  }

  const handleCloseAddNode = () => {
    handleCloseAddMenu()
    setNewNode(undefined)
  }

  const saveNode = (newData: DataNode[]) => {
    const newChapter: Chapter = {
      ...chapter,
      data: newData,
    }
    const newReport: Report = updateReportNumbering({
      ...report,
      chapter: updateChapter(report.chapter, newChapter),
    })
    updateReport(newReport)
  }

  const handleAddNode = () => {
    if (newNode) {
      let newData: DataNode[]
      const nodeExists = chapter.data.some((item) => item.id === newNode.id)
      if (nodeExists) {
        newData = chapter.data.map((item) => (item.id === newNode.id ? newNode : item))
      } else {
        newData = [...chapter.data, newNode]
      }
      saveNode(newData)
    }
    handleCloseAddNode()
  }

  const handleDeleteNode = (nodeId: string) => {
    const newData = chapter.data.filter((item) => item.id !== nodeId)
    saveNode(newData)
  }

  const handleOpenAddTextBlock = () => {
    setOpenAddTextBlockDialog(textBlocksForChapter)
    handleCloseAddMenu()
  }

  const handleAddTextBlock = (block: TextBlock) => {
    const newDataNodes: DataNode[] = block.block.map((dataNode) => ({
      ...dataNode,
    }))
    const newData = [...chapter.data, ...newDataNodes]
    saveNode(newData)
    handleCloseAddMenu()
  }

  const handleOpenAddSubChapter = () => {
    setChapterName("")
    setIsEditMode(false)
    setOpenEditChapterDialog(true)
    handleCloseAddMenu()
  }

  const handleOpenRenameChapter = () => {
    setChapterName(chapter.name)
    setIsEditMode(true)
    setOpenEditChapterDialog(true)
    handleCloseAddMenu()
  }

  const handleCloseEditChapter = () => {
    setOpenEditChapterDialog(false)
    setChapterName("")
    setIsEditMode(false)
  }

  const handleSaveChapter = () => {
    if (!chapterName.trim()) return

    if (isEditMode) {
      // Rename existing chapter
      const updateChapterInReport = (
        chapters: Chapter[],
        chapterId: string,
        newName: string
      ): Chapter[] => {
        return chapters.map((ch) => {
          if (ch.id === chapterId) {
            return { ...ch, name: newName }
          }
          if (ch.subChapter && ch.subChapter.length > 0) {
            return {
              ...ch,
              subChapter: updateChapterInReport(ch.subChapter, chapterId, newName),
            }
          }
          return ch
        })
      }

      const newReport: Report = {
        ...report,
        chapter: updateChapterInReport(report.chapter, chapter.id, chapterName.trim()),
      }

      updateReport(newReport)
    } else {
      // Add new subchapter
      const currentLevel = findChapterLevelById(report.chapter, chapter.id)

      // Check if we're at maximum level
      if (currentLevel !== null && currentLevel >= maxSubChapterLevel) {
        return // Should not happen due to disabled button, but safety check
      }

      const newSubChapter: Chapter = {
        id: v4(),
        examinationId: chapter.examinationId,
        type: ChapterType.OWN_CHAPTER,
        name: chapterName.trim(),
        chapterNumber: generateNextChapterNumber(chapter.chapterNumber, chapter.subChapter || []),
        subChapter: [],
        open: false,
        data: [],
      }

      const updatedChapter: Chapter = {
        ...chapter,
        subChapter: [...(chapter.subChapter || []), newSubChapter],
      }

      const newReport: Report = updateReportNumbering({
        ...report,
        chapter: updateChapter(report.chapter, updatedChapter),
      })

      updateReport(newReport)
    }

    handleCloseEditChapter()
  }

  // Check if we can add subchapters (max 4 levels deep)
  const canAddSubChapter = () => {
    const currentLevel = findChapterLevelById(report.chapter, chapter.id)
    return currentLevel !== null && currentLevel < maxSubChapterLevel
  }

  // Check if this chapter can be deleted
  const canDeleteChapter = () => {
    return deletableChapterTypes.includes(chapter.type)
  }

  // Check if this chapter can be renamed
  const canRenameChapter = () => {
    return editableChapterTypes.includes(chapter.type)
  }

  const handleOpenDeleteChapter = () => {
    setOpenDeleteChapterDialog(true)
    handleCloseAddMenu()
  }

  const handleCloseDeleteChapter = () => {
    setOpenDeleteChapterDialog(false)
  }

  const handleDeleteChapter = () => {
    // Find and remove the chapter from the report
    const removeChapterFromReport = (chapters: Chapter[], chapterId: string): Chapter[] => {
      return chapters.filter((ch) => {
        if (ch.id === chapterId) {
          return false // Remove this chapter
        }
        if (ch.subChapter && ch.subChapter.length > 0) {
          // Recursively check subchapters
          ch.subChapter = removeChapterFromReport(ch.subChapter, chapterId)
        }
        return true // Keep this chapter
      })
    }

    const chaptersAfterDeletion = removeChapterFromReport(report.chapter, chapter.id)

    // Use the proper chapter numbering function that handles appendix chapters correctly
    const newReport: Report = updateReportNumbering({
      ...report,
      chapter: chaptersAfterDeletion,
    })

    // Apply proper chapter numbering (including appendix letter numbering)
    const finalReport = updateReportChapterNumbering(newReport)

    updateReport(finalReport)
    handleCloseDeleteChapter()
  }

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !chapter.subChapter) {
      return
    }

    const { source, destination } = result

    // Only allow reordering of OWN_CHAPTER types
    const sourceChapter = chapter.subChapter[source.index]
    if (sourceChapter.type !== ChapterType.OWN_CHAPTER) {
      return
    }

    // Create a new array with reordered subchapters
    const reorderedSubChapters = Array.from(chapter.subChapter)
    const [removed] = reorderedSubChapters.splice(source.index, 1)
    reorderedSubChapters.splice(destination.index, 0, removed)

    // Update chapter numbers based on new order
    const updatedSubChapters = reorderedSubChapters.map((subChapter, index) => {
      // Generate the correct chapter number based on parent and position
      const newChapterNumber = `${chapter.chapterNumber}.${index + 1}`

      // Recursively update subchapter numbers if they exist
      const updateSubChapterNumbers = (subChapter: Chapter, parentNumber: string): Chapter => {
        if (subChapter.subChapter && subChapter.subChapter.length > 0) {
          return {
            ...subChapter,
            chapterNumber: parentNumber,
            subChapter: subChapter.subChapter.map((subSubChapter, subIndex) =>
              updateSubChapterNumbers(subSubChapter, `${parentNumber}.${subIndex + 1}`)
            ),
          }
        }
        return {
          ...subChapter,
          chapterNumber: parentNumber,
        }
      }

      return updateSubChapterNumbers(subChapter, newChapterNumber)
    })

    // Update the current chapter with new subchapters
    const updatedChapter: Chapter = {
      ...chapter,
      subChapter: updatedSubChapters,
    }

    const newReport: Report = updateReportNumbering({
      ...report,
      chapter: updateChapter(report.chapter, updatedChapter),
    })

    updateReport(newReport)
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* Chapter Title */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant={`${getChapterLevel(chapter.id)}`}>
          {chapter.chapterNumber}
          {chapter.type === ChapterType.APPENDIX_A || chapter.type === ChapterType.APPENDIX
            ? " - "
            : " "}
          {chapter.name}
        </Typography>

        <Box>
          <Tooltip title="Kapitel bearbeiten">
            <IconButton size="small" onClick={handleClickAddMenu}>
              <MenuIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            id="add-menu"
            anchorEl={anchorElAddMenu}
            open={openAddMenu}
            onClose={handleCloseAddMenu}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            <MenuItem onClick={() => handleOpenAddNode(NodeDataType.IMAGE)}>
              <Add /> Bilder
            </MenuItem>
            <MenuItem onClick={() => handleOpenAddNode(NodeDataType.TABLE)}>
              <Add /> Tabelle
            </MenuItem>
            <MenuItem onClick={() => handleOpenAddNode(NodeDataType.TEXT)}>
              <Add /> Text
            </MenuItem>
            <MenuItem
              disabled={textBlocksForChapter.length === 0}
              onClick={() => handleOpenAddTextBlock()}
            >
              <Add /> Textvorlage
            </MenuItem>
            <Divider />
            <MenuItem disabled={!canAddSubChapter()} onClick={() => handleOpenAddSubChapter()}>
              <Add /> Unterkapitel hinzufügen
            </MenuItem>
            <MenuItem disabled={!canRenameChapter()} onClick={() => handleOpenRenameChapter()}>
              <Edit /> Kapitel bearbeiten
            </MenuItem>
            <MenuItem disabled={!canDeleteChapter()} onClick={() => handleOpenDeleteChapter()}>
              <Delete /> Kapitel löschen
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Chapter Content */}
      <Box>
        <ReportDataContent
          chapter={chapter}
          onEdit={handleOpenAddNode}
          onDelete={handleDeleteNode}
          onSave={saveNode}
        />
      </Box>

      {/* Subchapter Content */}
      {chapter.subChapter && chapter.subChapter.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`subchapters-${chapter.id}`}>
              {(provided, snapshot) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    backgroundColor: snapshot.isDraggingOver
                      ? "rgba(25, 118, 210, 0.05)"
                      : "transparent",
                    borderRadius: 1,
                    transition: "background-color 0.2s ease",
                    p: 0,
                  }}
                >
                  {chapter.subChapter!.map((subChapter, index) => {
                    const isDraggable = subChapter.type === ChapterType.OWN_CHAPTER

                    return (
                      <Draggable
                        key={subChapter.id}
                        draggableId={subChapter.id}
                        index={index}
                        isDragDisabled={!isDraggable}
                      >
                        {(provided, snapshot) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{
                              opacity: snapshot.isDragging ? 0.8 : 1,
                              transform: snapshot.isDragging ? "rotate(2deg)" : "none",
                              transition: "all 0.2s ease",
                              mb: 1,
                            }}
                          >
                            <Box
                              {...(isDraggable ? provided.dragHandleProps : {})}
                              sx={{
                                cursor: isDraggable ? "grab" : "default",
                                "&:active": {
                                  cursor: isDraggable ? "grabbing" : "default",
                                },
                                position: "relative",
                              }}
                            >
                              <ReportContent
                                chapter={subChapter}
                                report={report}
                                updateReport={updateReport}
                              />
                            </Box>
                          </Box>
                        )}
                      </Draggable>
                    )
                  })}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      )}

      {newNode && isNodeText(newNode) && (
        <ReportNodeTextEditDialog
          open={newNode.dataType === NodeDataType.TEXT}
          onClose={handleCloseAddNode}
          node={newNode}
          setNode={setNewNode}
          onSave={handleAddNode}
        />
      )}

      {newNode && isNodeTable(newNode) && (
        <ReportNodeTableEditDialog
          open={newNode.dataType === NodeDataType.TABLE}
          onClose={handleCloseAddNode}
          node={newNode}
          setNode={setNewNode}
          onSave={handleAddNode}
        />
      )}

      {newNode && isNodeImage(newNode) && (
        <ReportNodeImageEditDialog
          open={newNode.dataType === NodeDataType.IMAGE}
          onClose={handleCloseAddNode}
          node={newNode}
          setNode={setNewNode}
          onSave={handleAddNode}
        />
      )}

      {openAddTextBlockDialog.length > 0 && (
        <AddBlockToChapter
          textBlocks={openAddTextBlockDialog}
          open={openAddTextBlockDialog.length > 0}
          onClose={() => setOpenAddTextBlockDialog([])}
          onSave={handleAddTextBlock}
        />
      )}

      {/* Edit Chapter Dialog */}
      <Dialog open={openEditChapterDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? "Kapitel bearbeiten" : "Unterkapitel hinzufügen"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Kapitelname"
            fullWidth
            variant="outlined"
            value={chapterName}
            onChange={(e) => setChapterName(e.target.value)}
            placeholder={
              isEditMode
                ? "Geben Sie den neuen Namen des Kapitels ein"
                : "Geben Sie den Namen des Unterkapitels ein"
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditChapter}>Abbrechen</Button>
          <Button onClick={handleSaveChapter} variant="contained" disabled={!chapterName.trim()}>
            {isEditMode ? "Speichern" : "Hinzufügen"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Chapter Dialog */}
      <DeleteDialog
        open={openDeleteChapterDialog}
        onClose={handleCloseDeleteChapter}
        onDelete={handleDeleteChapter}
      />
    </Box>
  )
}
